import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../helper/apiCall';
import ENDPOINTS from '../../constants/endpoints';

// Önerilen: Bağlantıyı garanti et (otomatik karar verir)
export const ensureWpConnection = createAsyncThunk(
  'wpConfig/ensureConnection',
  async (branchId, thunkAPI) => {
    try {
      const response = await client.post(`${ENDPOINTS.WP.ENSURE_CONNECTION}?branchId=${branchId}`, {}, {
        token: thunkAPI.getState().auth.token
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || error);
    }
  }
);

export const createWpInstance = createAsyncThunk(
  'wpConfig/createInstance',
  async (branchId, thunkAPI) => {
    try {
      const response = await client.post(`${ENDPOINTS.WP.CREATE_INSTANCE}?branchId=${branchId}`, {}, {
        token: thunkAPI.getState().auth.token
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || error);
    }
  }
);

export const fetchWpInstanceStatus = createAsyncThunk(
  'wpConfig/fetchInstanceStatus',
  async (branchId, thunkAPI) => {
    try {
      const response = await client.get(`${ENDPOINTS.WP.INSTANCE_STATUS}?branchId=${branchId}`, {
        token: thunkAPI.getState().auth.token
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || error);
    }
  }
);

export const reconnectWpInstance = createAsyncThunk(
  'wpConfig/reconnectInstance',
  async (branchId, thunkAPI) => {
    try {
      const response = await client.post(`${ENDPOINTS.WP.RECONNECT_INSTANCE}?branchId=${branchId}`, {}, {
        token: thunkAPI.getState().auth.token
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || error);
    }
  }
);

export const logoutWpInstance = createAsyncThunk(
  'wpConfig/logoutInstance',
  async (branchId, thunkAPI) => {
    try {
      const response = await client.post(`${ENDPOINTS.WP.LOGOUT_INSTANCE}?branchId=${branchId}`, {}, {
        token: thunkAPI.getState().auth.token
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || error);
    }
  }
);

export const fetchWpDebugInfo = createAsyncThunk(
  'wpConfig/fetchDebugInfo',
  async (branchId, thunkAPI) => {
    try {
      const response = await client.get(`${ENDPOINTS.WP.DEBUG}?branchId=${branchId}`, {
        token: thunkAPI.getState().auth.token
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || error);
    }
  }
);

export const sendWpMessage = createAsyncThunk(
  'wpConfig/sendMessage',
  async (payload, thunkAPI) => {
    try {
      // Bağlılık kontrolü (global uyarı amacıyla)
      const state = thunkAPI.getState();
      const instance = state.wpConfig.instance;
      const statusStr = instance?.status?.status || instance?.status || instance?.data?.status;
      const connected = (statusStr === 'connected') || instance?.connected === true || instance?.isConnected === true;
      if (!connected) {
        return thunkAPI.rejectWithValue('WP bağlı değil. Lütfen önce WP bağlayın.');
      }

      // branchId'yi payload yoksa state'ten enjekte et
      const fallbackBranchId = state.branches?.selectedBranch?.id;
      const body = { ...payload, branchId: payload?.branchId ?? fallbackBranchId };
      if (!body.branchId) {
        return thunkAPI.rejectWithValue('branchId bulunamadı. Lütfen bir branş seçiniz.');
      }

      const response = await client.post(
        ENDPOINTS.WP.SEND_MESSAGE,
        body,
        { token: state.auth.token }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || error);
    }
  }
);

const wpConfigSlice = createSlice({
  name: 'wpConfig',
  initialState: {
    status: 'idle',
    instance: null,
    error: null,
  },
  reducers: {
    clearWpError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Ensure Connection
      .addCase(ensureWpConnection.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(ensureWpConnection.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.instance = action.payload;
      })
      .addCase(ensureWpConnection.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Fetch Instance Status
      .addCase(fetchWpInstanceStatus.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchWpInstanceStatus.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.instance = action.payload;
      })
      .addCase(fetchWpInstanceStatus.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Create Instance
      .addCase(createWpInstance.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createWpInstance.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.instance = action.payload;
      })
      .addCase(createWpInstance.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Reconnect Instance
      .addCase(reconnectWpInstance.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(reconnectWpInstance.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.instance = action.payload;
      })
      .addCase(reconnectWpInstance.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Logout Instance
      .addCase(logoutWpInstance.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(logoutWpInstance.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.instance = null;
      })
      .addCase(logoutWpInstance.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const { clearWpError } = wpConfigSlice.actions;
export default wpConfigSlice.reducer; 